import { useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import classNames from 'classnames';
import dayjs from 'dayjs';
import datetime from 'utils/datetime';

import Form from '../components/_pages/booking/Form';
import Status from '../components/_pages/booking/Status';
import style from '../components/_pages/booking/style.module.scss';
import Button from '../components/ui/Button';
import LoadingOverlay from '../components/ui/LoadingOverlay';
import BookingPause from '../components/ui/icons/BookingPause';
import IncomingCall from '../components/ui/icons/IncomingCall';
import RestaurantTable from '../components/ui/icons/RestaurantTable';
import TableBooking from '../components/ui/icons/TableBooking';
import PageLayout from '../layouts/Page';
import useDeviceType from '../providers/DeviceType/hooks';
import i18n from '../providers/Locale';
import usePlace from '../providers/Place/hooks';
import { BookingSettings } from '../types/booking';

const getBgStyle = (bgImg: string): string => {
	return `url(${bgImg}), radial-gradient(circle, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.5) 100%)`;
};

type BookingPageType = {
	guid?: string;
};

const BookingPage: React.FC<BookingPageType> = () => {
	const { isMobile } = useDeviceType();
	const { guid } = useParams();
	const { place, loadError } = usePlace();
	const [bgImg] = useState<string>(
		place?.reservation?.media?.url ?? place?.companyImages?.desktop ?? ''
	);
	const [bookingSettings] = useState<BookingSettings | undefined>(place?.reservation);
	const containerStyle = useMemo(() => ({ backgroundImage: getBgStyle(bgImg) }), [isMobile, bgImg]);

	const nearestWorkingDate = useMemo(() => {
		return datetime.schedule.getNearestBusinessDay({
			workTimeAll: place?.workTime,
		});
	}, [place?.workTime, bookingSettings?.workingHours]);

	const outsideWorkingMessage = useMemo(() => {
		return i18n(
			'booking_outside_working_message',
			{
				time: dayjs(nearestWorkingDate).format('HH:mm'),
				day: datetime.format.dayLabel.getByDate(nearestWorkingDate),
			},
			true,
			{ isBold: true }
		);
	}, [nearestWorkingDate]);

	const noBookingBlock = useMemo(() => {
		return (
			<div
				className={classNames(style.noBooking, {
					[style.noBookingMobile]: true,
				})}>
				<div className={style.noBookingIcon}>
					<RestaurantTable size={64} />
				</div>
				<div className={style.noBookingTitle}>{i18n('booking.notWork.title')}</div>
				{place?.contactInfo?.phone && (
					<>
						<div className={style.noBookingDescription}>{i18n('booking.notWork.desc')}</div>
						<div className={style.noBookingPhoneBtn}>
							<a href={`tel:${place.contactInfo.phone}`}>
								<Button appearance={'primary'} size={'large'}>
									<IncomingCall />
									{place.contactInfo.phone}
								</Button>
							</a>
						</div>
					</>
				)}
			</div>
		);
	}, [place]);

	const placeDisabled = useMemo(
		() => (
			<div className={style.placeDisabled}>
				<div className={style.stopIcon} />
				<h3>{i18n('period_expired.title')}</h3>
				<p>{i18n('period_expired.description')}</p>
			</div>
		),
		[]
	);

	const noWorkingTimeBlock = useMemo(() => {
		return (
			<div className={style.Form}>
				<div className={style.notWork}>
					<BookingPause size={64} />
					<div className={style.notWorkTitle}>{i18n('booking_during_non_working')}</div>
					<span className={style.notWorkLabel}>
						{i18n(
							'booking_reservation_not_work',
							{
								time: dayjs(nearestWorkingDate).format('HH:mm'),
								day: datetime.format.dayLabel.getByDate(nearestWorkingDate),
							},
							true
						)}
					</span>
				</div>
			</div>
		);
	}, []);

	const statusBlock = useMemo(() => {
		if (!bookingSettings) return null;

		return (
			<Status
				outsideWorkingMessage={outsideWorkingMessage}
				showWorkingHoursMessage={bookingSettings.showWorkingHoursMessage}
			/>
		);
	}, [bookingSettings]);

	const formBlock = useMemo(() => {
		if (!bookingSettings) return null;

		return <Form bookingSettings={bookingSettings} />;
	}, [bookingSettings]);

	const content = useMemo(() => {
		if (!place || !bookingSettings) return null;

		if (guid) {
			return statusBlock;
		}

		if (!place?.opened && !bookingSettings.allowRequestOutsideWorkingHours) {
			return noWorkingTimeBlock;
		}

		return bookingSettings?.enabled ? formBlock : noBookingBlock;
	}, [place, bookingSettings, guid]);

	if ((!bookingSettings || !place) && !loadError) {
		return (
			<PageLayout>
				<div className={classNames([style.loaderWrapper])}>
					<LoadingOverlay />
				</div>
			</PageLayout>
		);
	}

	return (
		<PageLayout>
			<div className={classNames([style.BookingPage])}>
				<div className={style.container} style={containerStyle}>
					{place?.active && (
						<div className={style.content}>
							<div className={style.pageTitle}>
								<TableBooking />
								<div>{i18n('table_reservation')}</div>
							</div>
							{content}
						</div>
					)}
					{(!place || !place.active) && placeDisabled}
				</div>
			</div>
		</PageLayout>
	);
};

export default BookingPage;
